import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { AdminRunEventRow } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatCount } from '@/lib/format';
import {
  eventLabel,
  eventSummaryChips,
  eventTone,
  formatClockTime,
  formatOffsetMs,
} from '@/lib/runTelemetry';

const PAGE = 150;

function EventRow({ row }: { row: AdminRunEventRow }) {
  const [open, setOpen] = useState(false);
  const chips = eventSummaryChips(row.payload);
  const tone = eventTone(row.key);
  return (
    <li className="border-b border-border last:border-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="grid w-full cursor-pointer grid-cols-[1rem_4.5rem_1fr] items-start gap-3 px-4 py-2.5 text-left hover:bg-muted/30"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="mt-0.5 h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        ) : (
          <ChevronRight className="mt-0.5 h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        )}
        <span className="text-right font-mono text-xs tabular-nums text-muted-foreground">
          {formatOffsetMs(row.offsetMs) || formatClockTime(row.at)}
        </span>
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-2">
            {tone === 'neutral' ? (
              <span className="text-sm text-foreground">{eventLabel(row.key)}</span>
            ) : (
              <StatusPill tone={tone}>{eventLabel(row.key)}</StatusPill>
            )}
            <span className="font-mono text-[11px] text-muted-foreground">{row.key}</span>
          </span>
          {chips.length > 0 ? (
            <span className="mt-1 flex flex-wrap gap-1.5">
              {chips.map((chip) => (
                <span
                  key={chip.key}
                  className="max-w-full truncate rounded border border-border px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
                >
                  <span className="opacity-70">{chip.key}</span> {chip.value}
                </span>
              ))}
            </span>
          ) : null}
        </span>
      </button>
      {open ? (
        <pre className="mx-4 mb-3 max-h-80 overflow-auto rounded-md border border-border bg-background p-3 font-mono text-[11px] leading-5 text-muted-foreground">
          {JSON.stringify(row.payload, null, 2)}
        </pre>
      ) : null}
    </li>
  );
}

export function RunEventLog({
  events,
  truncated,
  heartbeatCount,
  streamLineCount,
}: {
  events: AdminRunEventRow[];
  truncated: boolean;
  heartbeatCount: number;
  streamLineCount: number;
}) {
  const [query, setQuery] = useState('');
  const [hideTools, setHideTools] = useState(true);
  const [visible, setVisible] = useState(PAGE);

  const toolCount = useMemo(() => events.filter((row) => row.key === 'agent.tool').length, [events]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return events.filter((row) => {
      if (hideTools && row.key === 'agent.tool') return false;
      if (!needle) return true;
      return `${row.key} ${eventLabel(row.key)}`.toLowerCase().includes(needle);
    });
  }, [events, hideTools, query]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setVisible(PAGE);
          }}
          placeholder="Filter by event or phase"
          aria-label="Filter events"
          className="h-9 max-w-xs"
        />
        {toolCount > 0 ? (
          <Button type="button" size="sm" variant="outline" onClick={() => setHideTools((value) => !value)}>
            {hideTools ? `Show ${formatCount(toolCount)} tool calls` : 'Hide tool calls'}
          </Button>
        ) : null}
        <p className="text-xs text-muted-foreground">
          {formatCount(filtered.length)} shown · {formatCount(heartbeatCount)} heartbeats and{' '}
          {formatCount(streamLineCount)} agent stream lines hidden
          {truncated ? ' · capped at the first 5,000 events' : ''}
        </p>
      </div>
      <ul className="overflow-hidden rounded-lg border border-border">
        {filtered.length === 0 ? (
          <li className="px-4 py-8 text-center text-sm text-muted-foreground">No events match</li>
        ) : (
          filtered.slice(0, visible).map((row) => <EventRow key={row.id} row={row} />)
        )}
      </ul>
      {filtered.length > visible ? (
        <Button type="button" size="sm" variant="ghost" onClick={() => setVisible((value) => value + PAGE)}>
          Show {formatCount(Math.min(PAGE, filtered.length - visible))} more
        </Button>
      ) : null}
    </div>
  );
}
